/**
 * ViewTable Component
 *
 * Table viewer for the workspace overlay with three modes:
 * 1. Versions - View and manage file versions for the table
 * 2. Data - View table records with server-side pagination/filtering
 * 3. Changes - Compare two versions to see differences (added/removed/modified records)
 *
 * Features:
 * - Auto-selects "last" tagged version or most recent version for data viewing
 * - Server-side data grid for versions and records
 * - Version comparison with diff visualization
 * - Tag management for versions
 *
 * Note: This component is only used within the workspace overlay (ExpandedItemContent).
 * There is no standalone page route for viewing tables.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Files, Table as TableIcon, GitCompare, Settings, Activity } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@loader/ui';
import type { VersionOption } from '@loader/ui';

import { useSidePanelStore } from '@/features/workspace/stores';
import {
  VersionsTable,
  VersionComparisonPickers,
  ChangesView,
  DataView,
  SchemaView,
  OperationsView,
} from '@/features/tables/components';
import {
  useTableQuery,
  useTableVersionsQuery,
  useVersionDiffQuery,
} from '@/features/tables/hooks/useTableQueries';
import { useWorkspaceMembership } from '@/hooks';
import { queryKeys } from '@/lib/queryKeys';

// ============================================================================
// Types
// ============================================================================

/**
 * View modes for the table page
 */
type ViewMode = 'versions' | 'table' | 'edits' | 'schema' | 'operations';

/**
 * Filter options for Changes view (UI tab)
 */
type ChangeFilter = 'all' | 'added' | 'removed';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Find the "current" tagged version or fallback to most recent completed version
 *
 * Only considers completed versions to avoid selecting versions still processing.
 */
const findCurrentVersion = (versions: VersionOption[]): VersionOption | null => {
  if (!versions || versions.length === 0) return null;

  // Filter to only completed versions (parquet file exists and is queryable)
  const completedVersions = versions.filter((v) => v.status === 'completed');
  if (completedVersions.length === 0) return null;

  // Look for version with "current" system tag among completed versions
  const currentTagged = completedVersions.find((u) => u.system_tag === 'current');

  // Fallback to first completed version (most recent)
  return currentTagged ?? completedVersions[0];
};

/**
 * Find the "previous" tagged version or fallback to second most recent completed version
 *
 * Only considers completed versions to avoid selecting versions still processing.
 */
const findPreviousVersion = (versions: VersionOption[]): VersionOption | null => {
  if (!versions || versions.length === 0) return null;

  // Filter to only completed versions (parquet file exists and is queryable)
  const completedVersions = versions.filter((v) => v.status === 'completed');
  if (completedVersions.length === 0) return null;

  // Look for version with "previous" system tag among completed versions
  const previousTagged = completedVersions.find((u) => u.system_tag === 'previous');

  // Fallback to second completed version, or first if only one exists
  return previousTagged ?? completedVersions[1] ?? completedVersions[0];
};

// ============================================================================
// Main Component
// ============================================================================

interface ViewTableContentProps {
  /** Table ID to display */
  tableId: string;
  /** Whether the user can edit (optional - component uses useWorkspaceMembership internally) */
  canEdit?: boolean;
}

const ViewTableContent: React.FC<ViewTableContentProps> = ({ tableId }) => {
  // ============================================================================
  // Hooks & State
  // ============================================================================

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const closePanel = useSidePanelStore((state) => state.close);

  // Fetch workspace membership to determine edit permissions
  const { canEdit } = useWorkspaceMembership();

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [changeFilter, setChangeFilter] = useState<ChangeFilter>('all');

  // Version selection state (for data viewing and comparison)
  const [selectedVersionA, setSelectedVersionA] = useState<VersionOption | null>(
    null
  );
  const [selectedVersionB, setSelectedVersionB] = useState<VersionOption | null>(
    null
  );

  // Pagination, sorting, and filtering state for Changes view (UI tab)
  const [diffPageIndex, setDiffPageIndex] = useState(0);
  const [diffPageSize, setDiffPageSize] = useState(50);
  const [diffSortBy, setDiffSortBy] = useState<string | undefined>(undefined);
  const [diffSortDir, setDiffSortDir] = useState<'asc' | 'desc' | undefined>(
    undefined
  );

  // ============================================================================
  // Data Fetching
  // ============================================================================

  // Fetch table metadata
  const {
    data: table,
    isLoading: isLoadingTable,
    error: tableError,
  } = useTableQuery(tableId);

  // Fetch table versions (needed for version picker)
  const { data: tableVersionsData, isLoading: isLoadingVersions } =
    useTableVersionsQuery(tableId, {
      enabled: !!tableId,
    });

  // Ensure versions is always an array (handles race conditions during navigation)
  // Memoized to maintain referential stability for useEffect dependencies
  const tableVersions = useMemo(
    () => (Array.isArray(tableVersionsData) ? tableVersionsData : []),
    [tableVersionsData]
  );

  // Filter for completed versions only - these are the only ones selectable in pickers
  const completedVersions = tableVersions.filter((v) => v.status === 'completed');

  // Fetch diff between Version A and Version B (only in edits mode)
  // Note: The "table" mode uses DataView component which handles data fetching
  // via useDataGrid internally, so no need to fetch records here
  const {
    data: diffData,
    isLoading: isDiffLoading,
    error: diffError,
  } = useVersionDiffQuery(tableId, selectedVersionB?.id, selectedVersionA?.id, {
    enabled:
      viewMode === 'edits' && !!selectedVersionA?.id && !!selectedVersionB?.id,
    limit: diffPageSize,
    offset: diffPageIndex * diffPageSize,
    sortBy: diffSortBy,
    sortDir: diffSortDir,
    changeType: changeFilter,
  });

  // ============================================================================
  // Derived State
  // ============================================================================

  // (No legacy format needed for new server-side implementation)

  // Compute loading and error states
  const isLoading = isLoadingTable || isLoadingVersions;
  const errorMessage = tableError
    ? tableError instanceof Error
      ? tableError.message
      : 'Failed to load table'
    : null;

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Auto-select versions when data becomes available
   * - Version A: Auto-select for both 'table' and 'edits' modes
   * - Version B: Auto-select only for 'edits' mode (needed for comparison)
   *
   * Also handles the case where a selected version was deleted:
   * - If selectedVersionA is no longer in the list, reset to the "last" version
   * - If selectedVersionB is no longer in the list, reset to the "previous" version
   */
  useEffect(() => {
    const versions = tableVersions as VersionOption[];
    if (!versions || versions.length === 0) {
      // No versions available, reset selections
      if (selectedVersionA) setSelectedVersionA(null);
      if (selectedVersionB) setSelectedVersionB(null);
      return;
    }

    const versionIds = new Set(versions.map((v) => v.id));

    // Check if selected Version A still exists in the list
    if (selectedVersionA && !versionIds.has(selectedVersionA.id)) {
      // Version A was deleted, reset to the "current" version
      const currentVersion = findCurrentVersion(versions);
      setSelectedVersionA(currentVersion);
    }

    // Check if selected Version B still exists in the list
    if (selectedVersionB && !versionIds.has(selectedVersionB.id)) {
      // Version B was deleted, reset to the "previous" version
      const previousVersion = findPreviousVersion(versions);
      setSelectedVersionB(previousVersion);
    }

    // Auto-select Version A for data viewing (table mode) and comparison (edits mode)
    if ((viewMode === 'table' || viewMode === 'edits') && !selectedVersionA) {
      const currentVersion = findCurrentVersion(versions);
      if (currentVersion) {
        setSelectedVersionA(currentVersion);
      }
    }

    // Auto-select Version B only for comparison (edits mode)
    if (viewMode === 'edits' && !selectedVersionB) {
      const previousVersion = findPreviousVersion(versions);
      if (previousVersion) {
        setSelectedVersionB(previousVersion);
      }
    }
  }, [tableVersions, viewMode, selectedVersionA, selectedVersionB]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handle navigation to version creation page
   * Navigates first, then closes the overlay
   */
  const handleCreateVersion = useCallback(() => {
    void navigate({
      to: '/versions/new',
      search: { tableId },
    }).then(() => {
      closePanel();
    });
  }, [closePanel, navigate, tableId]);

  /**
   * Handle version tags update - invalidate queries to refetch fresh data
   */
  const handleTagsUpdated = useCallback(() => {
    if (tableId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tables.versions(tableId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tables.detail(tableId),
      });
    }
  }, [tableId, queryClient]);

  /**
   * Handle pagination changes for diff (Changes view UI tab)
   */
  const handleDiffPaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setDiffPageIndex(page);
      setDiffPageSize(pageSize);
    },
    []
  );

  /**
   * Handle sort changes for diff (Changes view UI tab)
   */
  const handleDiffSortChange = useCallback(
    (sortBy: string | undefined, sortDir?: 'asc' | 'desc') => {
      setDiffSortBy(sortBy);
      setDiffSortDir(sortDir);
    },
    []
  );

  /**
   * Handle filter changes for diff (Changes view UI tab)
   */
  const handleDiffFilterChange = useCallback((filter: ChangeFilter) => {
    setChangeFilter(filter);
    // Reset to first page when filter changes
    setDiffPageIndex(0);
  }, []);

  // ============================================================================
  // Early Returns (Loading, Error, Not Found)
  // ============================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Loading table...</div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-red-500">Error: {errorMessage}</div>
      </div>
    );
  }

  if (!table) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Table not found</div>
      </div>
    );
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Controls Section */}
        <div className="flex flex-col gap-4 px-4 pt-4">
          {/* View Mode Toolbar */}
          <ViewModeToolbar
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onCreateVersion={handleCreateVersion}
            canEdit={canEdit}
          />

          {/* Version Comparison Pickers (shown in Data and Changes modes) */}
          {viewMode !== 'versions' && (
            <VersionComparisonPickers
              selectedA={selectedVersionA}
              selectedB={selectedVersionB}
              onSelectA={setSelectedVersionA}
              onSelectB={setSelectedVersionB}
              versions={completedVersions as VersionOption[]}
              showVersionA={viewMode === 'table' || viewMode === 'edits'}
              showVersionB={viewMode === 'edits'}
            />
          )}
        </div>

        {/* Main Content Area */}
        <MainContentArea
          viewMode={viewMode}
          tableId={tableId}
          tableName={table?.name}
          selectedVersionId={selectedVersionA?.id}
          changeFilter={changeFilter}
          onChangeFilterChange={handleDiffFilterChange}
          onTagsUpdated={handleTagsUpdated}
          canEdit={canEdit}
          // Server-side diff props
          diffData={diffData}
          isDiffLoading={isDiffLoading}
          diffError={diffError}
          fromVersionId={selectedVersionB?.id}
          toVersionId={selectedVersionA?.id}
          onDiffPaginationChange={handleDiffPaginationChange}
          onDiffSortChange={handleDiffSortChange}
          diffPageIndex={diffPageIndex}
          diffPageSize={diffPageSize}
          diffSortBy={diffSortBy}
          diffSortDir={diffSortDir}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * View Mode Toolbar Component
 * Provides buttons to switch between Versions, Data, and Changes views (UI tabs)
 */
interface ViewModeToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onCreateVersion: () => void;
  /** Whether user can edit - viewers don't see New Version button */
  canEdit?: boolean;
}

const ViewModeToolbar: React.FC<ViewModeToolbarProps> = ({
  viewMode,
  onViewModeChange,
  onCreateVersion,
  canEdit = true,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-4">
    {/* View Mode Selector Buttons */}
    <div className="flex items-center gap-2">
      {/* Data group: Data, Changes */}
      <Button
        variant={viewMode === 'table' ? 'default' : 'outline'}
        size="sm"
        className="w-28 justify-center"
        onClick={() => onViewModeChange('table')}
      >
        <TableIcon className="mr-2 h-4 w-4" />
        Data
      </Button>
      <Button
        variant={viewMode === 'edits' ? 'default' : 'outline'}
        size="sm"
        className="w-28 justify-center"
        onClick={() => onViewModeChange('edits')}
      >
        <GitCompare className="mr-2 h-4 w-4" />
        Changes
      </Button>

      {/* Separator */}
      <div className="h-6 w-px bg-border" />

      {/* Metadata group: Versions, Schema, Operations */}
      <Button
        variant={viewMode === 'versions' ? 'default' : 'outline'}
        size="sm"
        className="w-28 justify-center"
        onClick={() => onViewModeChange('versions')}
      >
        <Files className="mr-2 h-4 w-4" />
        Versions
      </Button>
      <Button
        variant={viewMode === 'schema' ? 'default' : 'outline'}
        size="sm"
        className="w-28 justify-center"
        onClick={() => onViewModeChange('schema')}
      >
        <Settings className="mr-2 h-4 w-4" />
        Schema
      </Button>
      <Button
        variant={viewMode === 'operations' ? 'default' : 'outline'}
        size="sm"
        className="w-32 justify-center"
        onClick={() => onViewModeChange('operations')}
      >
        <Activity className="mr-2 h-4 w-4" />
        Operations
      </Button>
    </div>

    {/* New Version Button (only shown in versions mode for editors/owners) */}
    {viewMode === 'versions' && canEdit && (
      <Button size="sm" onClick={onCreateVersion}>
        <Files className="mr-2 h-4 w-4" />
        New Version
      </Button>
    )}
  </div>
);

/**
 * Main Content Area Component
 * Renders the appropriate view (UI tab) based on selected view mode
 */
interface MainContentAreaProps {
  viewMode: ViewMode;
  tableId: string;
  tableName?: string;
  selectedVersionId?: string;
  changeFilter: ChangeFilter;
  onChangeFilterChange: (filter: ChangeFilter) => void;
  onTagsUpdated: () => void;
  /** Whether user can edit - viewers see read-only schema */
  canEdit?: boolean;
  // Server-side diff props (Changes view UI tab)
  diffData?: {
    data: Record<string, unknown>[];
    counts: {
      added: number;
      removed: number;
      unchanged: number;
    };
    pagination: {
      limit: number;
      offset: number;
      total: number;
      page: number;
      pages: number;
    };
  };
  isDiffLoading?: boolean;
  diffError?: Error | null;
  fromVersionId?: string;
  toVersionId?: string;
  onDiffPaginationChange?: (page: number, pageSize: number) => void;
  onDiffSortChange?: (
    sortBy: string | undefined,
    sortDir?: 'asc' | 'desc'
  ) => void;
  diffPageIndex?: number;
  diffPageSize?: number;
  diffSortBy?: string;
  diffSortDir?: 'asc' | 'desc';
}

const MainContentArea: React.FC<MainContentAreaProps> = ({
  viewMode,
  tableId,
  tableName,
  selectedVersionId,
  changeFilter,
  onChangeFilterChange,
  onTagsUpdated,
  canEdit = true,
  // Server-side diff props
  diffData,
  isDiffLoading = false,
  diffError = null,
  fromVersionId,
  toVersionId,
  onDiffPaginationChange,
  onDiffSortChange,
  diffPageIndex = 0,
  diffPageSize = 50,
  diffSortBy,
  diffSortDir,
}) => {
  // Render Versions view (UI tab)
  if (viewMode === 'versions') {
    return (
      <div className="min-h-0 flex-1">
        <VersionsTable tableId={tableId} onTagsUpdated={onTagsUpdated} variant="flush" canEdit={canEdit} />
      </div>
    );
  }

  // Render Data view (UI tab)
  if (viewMode === 'table') {
    return (
      <div className="min-h-0 flex-1">
        <DataView
          tableId={tableId}
          // Pass versionId for server-side filtering by version
          versionId={selectedVersionId}
          variant="flush"
        />
      </div>
    );
  }

  // Render Changes view (UI tab) with server-side pagination, filtering, and sorting
  if (viewMode === 'edits' && fromVersionId && toVersionId) {
    return (
      <ChangesView
        tableId={tableId}
        tableName={tableName}
        fromVersionId={fromVersionId}
        toVersionId={toVersionId}
        diffData={diffData}
        isLoading={isDiffLoading}
        error={diffError}
        changeFilter={changeFilter}
        onFilterChange={onChangeFilterChange}
        onPaginationChange={
          onDiffPaginationChange || (() => {})
        }
        onSortChange={onDiffSortChange || (() => {})}
        pageIndex={diffPageIndex}
        pageSize={diffPageSize}
        sortBy={diffSortBy}
        sortDir={diffSortDir}
        variant="flush"
      />
    );
  }

  // Render Schema view (UI tab)
  if (viewMode === 'schema') {
    return (
      <div className="min-h-0 flex-1">
        <SchemaView tableId={tableId} variant="flush" canEdit={canEdit} />
      </div>
    );
  }

  // Render Operations view (UI tab)
  if (viewMode === 'operations') {
    return (
      <div className="min-h-0 flex-1">
        <OperationsView tableId={tableId} variant="flush" />
      </div>
    );
  }

  return null;
};

// ============================================================================
// Exports
// ============================================================================

export { ViewTableContent };
